import { Box, Card, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import SockJS from "sockjs-client/dist/sockjs";
import Stomp from "stompjs";
import { AppColors } from "../../../../util/AppColors";
import { socketServer } from "../../../../util/server";
import {
  appendWebChatMessage,
  getWebChatList,
  getWebChatMessages,
  getWhatsAppInbox,
  markWebChatRead,
  sendWebChatMessage,
  sendWhatsAppMessage,
  setSelectedWebChatId,
  startWebChat,
  upsertWebChatInList,
  removeWebChatFromList,
} from "../../../../redux/reducers/webChatSlice";
import { getClinicVets } from "../../../../redux/reducers/vetSlice";
import { getClinicUsersWoLoader } from "../../../../redux/reducers/clinicSlice";
import ChatSidebar from "./components/ChatSidebar";
import ChatHeader from "./components/ChatHeader";
import ChatConversation from "./components/ChatConversation";
import ChatComposer from "./components/ChatComposer";
import ChatDetailsSidebar from "./components/ChatDetailsSidebar";
import {
  mapActiveChatToPatientDetails,
  mapActiveChatToPetDetails,
  mapWebChatListItemToUi,
  mapWebChatMessageToUi,
  mapWhatsAppInboxMessageToUi,
  mapWhatsAppThreads,
  phoneKey,
  resolveClinicIdForWebSocket,
  whatsAppCustomerPhone,
} from "./webChatMappers";
import {
  mockInvoices,
  mockRecentVisits,
  mockShortcuts,
} from "./mockChatData";

const isInternalSource = (source) =>
  String(source || "").toUpperCase() === "INTERNAL";

const isWhatsAppChat = (chat) =>
  !!chat &&
  (String(chat.id).startsWith("wa-") ||
    (!isInternalSource(chat.source) && !!phoneKey(chat.clientMobile)));

const Chat = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const stompClientRef = useRef(null);
  const selectedChatIdRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [activeLeftTab, setActiveLeftTab] = useState("internal");

  const role = localStorage.getItem("role");
  const profile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const clinicWsId = useMemo(
    () => resolveClinicIdForWebSocket(profile, role),
    [profile, role]
  );

  const { list, messagesByChatId, selectedChatId, whatsappInbox } = useSelector(
    (state) => state.webChat
  );
  const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const clinicUsers = useSelector((state) => state?.clinic?.users);

  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);

  const uiChats = useMemo(
    () => (list || []).map(mapWebChatListItemToUi).filter(Boolean),
    [list]
  );

  const whatsAppThreads = useMemo(
    () => mapWhatsAppThreads(whatsappInbox),
    [whatsappInbox]
  );

  const externalChats = useMemo(() => {
    // Only show chats the clinic has started/selected (web-chat list + local drafts).
    // Do not dump the full WhatsApp inbox into the sidebar.
    return uiChats
      .filter((c) => !isInternalSource(c.source))
      .map((chat) => {
        const key = phoneKey(chat.clientMobile);
        const thread = whatsAppThreads.find(
          (item) => phoneKey(item.clientMobile) === key
        );
        if (!key || !thread) return chat;
        return {
          ...chat,
          channel: "whatsapp",
          lastMessage: thread.lastMessage || chat.lastMessage,
          lastMessageTime: thread.lastMessageTime || chat.lastMessageTime,
        };
      });
  }, [uiChats, whatsAppThreads]);

  const internalChats = useMemo(
    () => uiChats.filter((c) => isInternalSource(c.source)),
    [uiChats]
  );

  const doctorsForInternal = useMemo(() => {
    const q = (searchText || "").trim().toLowerCase();
    // Only show staff directory while searching — to start a new chat
    if (!q) return [];

    const raw = Array.isArray(clinicVets) ? clinicVets : [];
    const chattedIds = new Set(
      internalChats.map((c) => String(c.clientUserId)).filter(Boolean)
    );

    const isAssistantVet = (vet) => {
      const roleRaw = vet?.role ?? vet?.userRole ?? vet?.roles;
      const roleList = Array.isArray(roleRaw)
        ? roleRaw
        : roleRaw != null
          ? [roleRaw]
          : [];
      return roleList.some((r) => {
        const val = String(r?.value ?? r?.name ?? r ?? "")
          .trim()
          .toLowerCase();
        return val === "assistant vet" || val === "assistantvet";
      });
    };

    return raw
      .filter((vet) => {
        const id = vet?.doctorId ?? vet?.id;
        if (!id || String(id) === String(profile?.id)) return false;
        if (isAssistantVet(vet)) return false;
        // Skip people already in the chat list (they appear in chats above)
        if (chattedIds.has(String(id))) return false;
        const types = vet?.userType || [];
        const isDoctor = types.some((ut) => ut?.name === "ROLE_DOCTOR");
        if (types.length > 0 && !isDoctor) return false;
        const name = (vet.doctorName || vet.name || "").toLowerCase();
        return name.includes(q) || String(id).includes(q);
      })
      .map((vet) => {
        const doctorId = vet.doctorId ?? vet.id;
        return {
          ...vet,
          doctorId,
          doctorName: vet.doctorName || vet.name,
          existingChatId: null,
          lastMessage: "",
        };
      });
  }, [clinicVets, profile?.id, internalChats, searchText]);

  const clientsForExternal = useMemo(() => {
    if (activeLeftTab !== "external") return [];
    const q = (searchText || "").trim().toLowerCase();
    if (q.length < 2) return [];
    const raw = Array.isArray(clinicUsers)
      ? clinicUsers
      : Array.isArray(clinicUsers?.users)
        ? clinicUsers.users
        : Array.isArray(clinicUsers?.data)
          ? clinicUsers.data
          : Array.isArray(clinicUsers?.content)
            ? clinicUsers.content
            : [];
    // Always show clinic/users search hits (do not hide if already in chat list)
    return raw
      .filter((user) => {
        const id = user?.id ?? user?.userId;
        const mobile = user.coOwnerMobile || user.mobile || "";
        if (!id && !mobile) return false;
        if (id && String(id) === String(profile?.id)) return false;
        return true;
      })
      .map((user) => {
        const id = user.id ?? user.userId ?? null;
        const mobile = user.coOwnerMobile || user.mobile || "";
        // WhatsApp identity is the phone — never reuse another chat by user id alone
        const existing = mobile
          ? externalChats.find(
              (c) =>
                c.clientMobile &&
                phoneKey(c.clientMobile) === phoneKey(mobile)
            )
          : null;
        return {
          id,
          name: user.coOwnerName || user.name || "Client",
          mobile,
          image: user.userImage || "",
          petId: user.petId || user?.pets?.[0]?.petId || null,
          existingChatId: existing?.id || null,
        };
      });
  }, [
    activeLeftTab,
    searchText,
    clinicUsers,
    externalChats,
    profile?.id,
  ]);

  const sidebarChats = useMemo(() => {
    if (activeLeftTab === "internal") {
      const q = (searchText || "").trim().toLowerCase();
      if (!q) return internalChats;
      return internalChats.filter((c) => {
        const name = `${c.patientName || ""} ${c.petName || ""}`.toLowerCase();
        return (
          name.includes(q) ||
          String(c.clientUserId || "").includes(q) ||
          String(c.id || "").includes(q)
        );
      });
    }
    // External: while searching, hide existing chats — only clinic/users results
    const q = (searchText || "").trim();
    if (q.length >= 2) return [];
    return externalChats;
  }, [activeLeftTab, externalChats, internalChats, searchText]);

  const activeChat =
    (activeLeftTab === "external" ? externalChats : internalChats).find(
      (c) => String(c.id) === String(selectedChatId)
    ) || null;

  const activeMessages = useMemo(() => {
    if (!selectedChatId || !activeChat) return [];
    const webMessages = String(selectedChatId).startsWith("wa-")
      ? []
      : (messagesByChatId[String(selectedChatId)] || [])
          .map((msg) => mapWebChatMessageToUi(msg, profile?.id))
          .filter(Boolean);
    if (!isWhatsAppChat(activeChat) || isInternalSource(activeChat.source)) {
      return webMessages;
    }
    const key = phoneKey(activeChat.clientMobile);
    const whatsAppMessages = (whatsappInbox || [])
      .filter((msg) => whatsAppCustomerPhone(msg) === key)
      .map(mapWhatsAppInboxMessageToUi)
      .filter(Boolean);
    if (String(selectedChatId).startsWith("wa-")) return whatsAppMessages;
    return [...webMessages, ...whatsAppMessages];
  }, [messagesByChatId, selectedChatId, profile?.id, activeChat, whatsappInbox]);

  const tabCounts = {
    internal: internalChats.length,
    external: externalChats.length,
  };

  const refreshList = useCallback(
    (opts = {}) => {
      // External text search uses clinic/users only — never pass sidebar search here
      const showLoading = opts.showLoading !== false;
      return dispatch(
        getWebChatList({ filter: "all", search: "", showLoading })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(getClinicVets("?type=doctor&itemSize=100&page=1"));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const state = location?.state || {};
      if (state.clientUserId) {
        const started = await dispatch(
          startWebChat({
            clientUserId: state.clientUserId,
            petId: state.petId,
            source: state.source || "WEB",
          })
        );
        if (!cancelled && started?.payload?.chatId) {
          dispatch(setSelectedWebChatId(started.payload.chatId));
        }
      } else if (state.chatId) {
        dispatch(setSelectedWebChatId(state.chatId));
      }
      if (!cancelled) {
        await refreshList({ showLoading: true });
        // Do not auto-select a chat — wait for the user to pick one
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeLeftTab !== "external") return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = (searchText || "").trim();
    if (q.length < 2) return undefined;
    searchDebounceRef.current = setTimeout(() => {
      // External search only: GET clinic/users?search=&itemSize=30&page=1&userData=true
      dispatch(
        getClinicUsersWoLoader(
          `?search=${encodeURIComponent(q)}&itemSize=30&page=1&userData=true`
        )
      );
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [activeLeftTab, searchText, dispatch]);

  useEffect(() => {
    if (activeLeftTab !== "external") return undefined;
    dispatch(getWhatsAppInbox());
    const timer = setInterval(() => {
      dispatch(getWhatsAppInbox());
    }, 5000);
    return () => clearInterval(timer);
  }, [activeLeftTab, dispatch]);

  // Drop selection if it is no longer in the started/selected external list
  useEffect(() => {
    if (activeLeftTab !== "external" || !selectedChatId) return;
    const stillThere = externalChats.some(
      (c) => String(c.id) === String(selectedChatId)
    );
    if (!stillThere) dispatch(setSelectedWebChatId(null));
  }, [activeLeftTab, externalChats, selectedChatId, dispatch]);

  useEffect(() => {
    if (!selectedChatId || String(selectedChatId).startsWith("wa-")) return;
    let cancelled = false;
    dispatch(
      getWebChatMessages({
        chatId: selectedChatId,
        showLoading: false,
      })
    ).then(() => {
      if (cancelled) return;
      dispatch(markWebChatRead(selectedChatId)).then(() => {
        if (!cancelled) refreshList({ showLoading: false });
      });
    });
    return () => {
      cancelled = true;
    };
    // Only reload when the selected chat changes — not when search text changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatId, dispatch]);

  useEffect(() => {
    let mounted = true;

    const connect = () => {
      const stompClient = Stomp.over(new SockJS(socketServer));
      stompClient.debug = () => {};
      stompClientRef.current = stompClient;

      stompClient.connect(
        {},
        () => {
          if (!mounted) return;

          if (clinicWsId) {
            stompClient.subscribe(
              `/topic/webchat/clinic/${clinicWsId}`,
              () => {
                refreshList({ showLoading: false });
              }
            );
          }

          if (profile?.id) {
            stompClient.subscribe(
              `/user/${profile.id}/topic/webchat/list`,
              () => {
                refreshList({ showLoading: false });
              }
            );
          }

          if (selectedChatId) {
            stompClient.subscribe(
              `/user/${selectedChatId}/topic/webchat/messages`,
              (frame) => {
                try {
                  const payload = JSON.parse(frame.body);
                  if (
                    payload?.chatId != null &&
                    String(payload.chatId) === String(selectedChatIdRef.current)
                  ) {
                    dispatch(appendWebChatMessage(payload));
                  }
                  refreshList({ showLoading: false });
                } catch (e) {
                  console.error("WebChat WS parse error", e);
                }
              }
            );
          }
        },
        (error) => {
          console.error("WebChat WebSocket error:", error);
        }
      );
    };

    connect();

    return () => {
      mounted = false;
      if (stompClientRef.current?.connected) {
        try {
          stompClientRef.current.disconnect();
        } catch (_) {
          /* ignore */
        }
      }
      stompClientRef.current = null;
    };
  }, [clinicWsId, profile?.id, selectedChatId, dispatch, refreshList]);

  const handleSelectChat = (id) => {
    dispatch(setSelectedWebChatId(id));
  };

  const handleSelectDoctor = async (doctor) => {
    const doctorId = doctor?.doctorId ?? doctor?.id;
    if (!doctorId) return;

    if (doctor.existingChatId) {
      dispatch(setSelectedWebChatId(doctor.existingChatId));
      return;
    }

    const started = await dispatch(
      startWebChat({
        clientUserId: doctorId,
        source: "INTERNAL",
      })
    );
    if (started?.payload?.chatId) {
      dispatch(setSelectedWebChatId(started.payload.chatId));
      refreshList({ showLoading: false });
    }
  };

  const handleSelectClient = async (client) => {
    const clientUserId = client?.id ?? client?.userId ?? null;
    const mobile = String(client?.mobile || "").trim();
    if (!clientUserId && !mobile) return;

    // Only reopen when the existing chat is the same WhatsApp number
    if (client.existingChatId) {
      dispatch(setSelectedWebChatId(client.existingChatId));
      setSearchText("");
      return;
    }

    const openId = `wa-${phoneKey(mobile) || clientUserId}`;
    const name = client?.name || "Client";
    const draft = {
      chatId: openId,
      clientUserId: clientUserId || null,
      clientName: name,
      clientMobile: mobile,
      clientImage: client?.image || "",
      petId: client?.petId || null,
      source: "WHATSAPP",
      whatsappSynced: true,
      lastMessage: "",
      unreadCount: 0,
      initials: name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join(""),
      _local: true,
    };

    // Open Whatsup (clicked client) immediately — do not jump to another contact
    dispatch(upsertWebChatInList(draft));
    dispatch(setSelectedWebChatId(openId));
    setSearchText("");

    if (!clientUserId) return;

    const started = await dispatch(
      startWebChat({
        clientUserId,
        petId: client.petId || undefined,
        source: "WHATSAPP",
        whatsappSynced: true,
        showLoading: false,
      })
    );

    const realId = started?.payload?.chatId;
    if (!realId) return;

    const apiMobile = String(started?.payload?.clientMobile || "").trim();
    const samePhone =
      !mobile ||
      !apiMobile ||
      phoneKey(apiMobile) === phoneKey(mobile);

    // API may return an unrelated chat for this userId — keep WhatsApp draft if phone differs
    if (!samePhone) {
      dispatch(setSelectedWebChatId(openId));
      return;
    }

    if (String(realId) !== String(openId)) {
      dispatch(removeWebChatFromList(openId));
    }
    dispatch(
      upsertWebChatInList({
        ...draft,
        ...started.payload,
        chatId: realId,
        // Always keep the searched client's name/number in the UI
        clientName: draft.clientName,
        clientMobile: draft.clientMobile || apiMobile,
        source: "WHATSAPP",
        whatsappSynced: true,
        _local: false,
      })
    );
    dispatch(setSelectedWebChatId(realId));
    dispatch(getWebChatMessages({ chatId: realId, showLoading: false }));
  };

  const handleSendMessage = async (text, files = []) => {
    if (!selectedChatId || !activeChat) return;
    const hasText = text && String(text).trim() !== "";
    const hasFiles = files && files.length > 0;
    if (!hasText && !hasFiles) return;

    if (
      activeLeftTab !== "internal" &&
      isWhatsAppChat(activeChat) &&
      hasText
    ) {
      const sent = await dispatch(
        sendWhatsAppMessage({
          to: activeChat.clientMobile,
          body: text.trim(),
        })
      );
      if (sendWhatsAppMessage.fulfilled.match(sent)) {
        dispatch(getWhatsAppInbox());
      } else if (sent?.payload) {
        window.alert(sent.payload);
      }
      if (!hasFiles || String(selectedChatId).startsWith("wa-")) return;
    }

    if (String(selectedChatId).startsWith("wa-")) return;

    let messageType = "TEXT";
    if (hasFiles) {
      const allImages = files.every((f) => f?.type?.startsWith("image/"));
      messageType = allImages ? "IMAGE" : "FILE";
    }

    await dispatch(
      sendWebChatMessage({
        chatId: selectedChatId,
        content: hasText ? text.trim() : "",
        messageType,
        files,
      })
    );
    refreshList({ showLoading: false });
  };

  const patientDetails = mapActiveChatToPatientDetails(activeChat);
  const petDetails = mapActiveChatToPetDetails(activeChat);

  return (
    <Box
      sx={{
        px: 1.5,
        pt: 1,
        pb: 0,
        height: "calc(100vh - 64px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid
        container
        spacing={1.5}
        sx={{ height: "100%", flexGrow: 1, flexWrap: "nowrap" }}
      >
        <Grid item xs={12} md={3.2} lg={3} xl={3} sx={{ height: "100%" }}>
          <Card
            sx={{
              borderColor: "#79C5EF61",
              borderWidth: "1px",
              borderStyle: "solid",
              height: "100%",
              borderRadius: "16px",
              boxShadow:
                "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)",
            }}
          >
            <ChatSidebar
              chats={sidebarChats}
              doctors={doctorsForInternal}
              clients={clientsForExternal}
              selectedChatId={selectedChatId}
              onSelectChat={handleSelectChat}
              onSelectDoctor={handleSelectDoctor}
              onSelectClient={handleSelectClient}
              searchText={searchText}
              onSearchChange={setSearchText}
              activeTab={activeLeftTab}
              onTabChange={setActiveLeftTab}
              tabCounts={tabCounts}
            />
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={activeLeftTab === "internal" ? 8.8 : 5.6}
          lg={activeLeftTab === "internal" ? 9 : 6}
          xl={activeLeftTab === "internal" ? 9 : 6}
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              borderColor: "#79C5EF61",
              borderWidth: "1px",
              borderStyle: "solid",
              height: "100%",
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
              boxShadow:
                "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)",
              overflow: "hidden",
            }}
          >
            {activeChat ? (
              <>
                <ChatHeader activeChat={activeChat} />
                <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden" }}>
                  <ChatConversation
                    messages={activeMessages}
                    showWhatsAppBanner={!isInternalSource(activeChat?.source)}
                  />
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  <ChatComposer onSendMessage={handleSendMessage} />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{ color: AppColors.gray5, fontFamily: "Montserrat" }}
                >
                  {activeLeftTab === "internal"
                    ? "Select a doctor to start an internal chat"
                    : "Select a conversation to start chatting"}
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {activeLeftTab !== "internal" && (
        <Grid item xs={12} md={3.2} lg={3} xl={3} sx={{ height: "100%" }}>
          <Card
            sx={{
              borderColor: "#79C5EF61",
              borderWidth: "1px",
              borderStyle: "solid",
              height: "100%",
              borderRadius: "16px",
              boxShadow:
                "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)",
            }}
          >
            <ChatDetailsSidebar
              patientDetails={patientDetails}
              petDetails={petDetails}
              recentVisits={mockRecentVisits}
              invoices={mockInvoices}
              shortcuts={mockShortcuts}
            />
          </Card>
        </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Chat;
